/**
 * 图形验证码弹窗组件
 */
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";
import HeadlessDialog from './Dialog/HeadlessDialog';
import Captcha, { CaptchaRef } from "./Captcha";

interface CaptchaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onVerifySuccess: (captchaUuid: string, captchaCode: string) => void;
  phone: string;
}

const CaptchaDialog = ({ isOpen, onClose, onVerifySuccess, phone }: CaptchaDialogProps) => {
  const [captchaCode, setCaptchaCode] = useState("");
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const captchaRef = useRef<CaptchaRef>(null);

  // 重置表单
  const resetForm = () => {
    setCaptchaCode("");
    setError("");
    setIsVerifying(false);
  };

  // 当对话框关闭时重置表单
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleVerify = async () => {
    if (!captchaCode.trim()) {
      setError("请输入图形验证码");
      return;
    }

    const captchaUuid = captchaRef.current?.getUuid();
    if (!captchaUuid) {
      setError("请刷新验证码");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      // 调用父组件的验证成功回调
      onVerifySuccess(captchaUuid, captchaCode);
      resetForm();
      onClose();
    } catch (err) {
      setError("验证失败，请重试");
      if (captchaRef.current) {
        captchaRef.current.refresh();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    if (!isVerifying) {
      resetForm();
      onClose();
    }
  };

  return (
    <HeadlessDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="图形验证"
      description={`即将向 ${phone} 发送验证码，请先完成图形验证`}
      size="sm"
      isShowClose={false}
    >
      <div className="space-y-6 py-4">
        <div>
          <Label htmlFor="captchaCode" className="flex items-center space-x-2 mb-2">
            <Shield className="w-4 h-4"/>
            <span>图形验证码 *</span>
          </Label>
          <div className="flex items-center space-x-3">
            <Input
              id="captchaCode"
              value={captchaCode}
              onChange={(e) => {
                setCaptchaCode(e.target.value);
                if (error) setError("");
              }}
              placeholder="请输入图形验证码"
              className={error ? "border-red-500" : ""}
              maxLength={4}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleVerify();
                }
              }}
            />
            <Captcha ref={captchaRef} />
          </div>
          {error && (
            <p className="text-sm text-red-500 mt-2">{error}</p>
          )}
        </div>

        <div className="flex space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isVerifying}
            className="flex-1"
          >
            取消
          </Button>
          <Button
            type="button"
            onClick={handleVerify}
            disabled={isVerifying || !captchaCode.trim()}
            className="flex-1"
          >
            {isVerifying ? "验证中..." : "确认"}
          </Button>
        </div>
      </div>
    </HeadlessDialog>
  );
};

export default CaptchaDialog;
